/**
 * localStorage helpers for the device-bound PIN login credential.
 *
 * Only the server-issued random `deviceId` (plus display name/email for the
 * "Welcome back" screen) is stored here — never the PIN itself. Login
 * requires both this deviceId and the PIN, so clearing storage simply
 * reverts the device to password login.
 */

export interface PinDevice {
  deviceId: string;
  email: string;
  name: string;
}

const PIN_DEVICE_KEY = "pinDevice";

// Dispatched on window when PIN devices are revoked server-side (e.g. after a
// password change) so open settings screens can refresh.
export const PIN_DEVICES_CHANGED_EVENT = "pin:devices-changed";

export const getPinDevice = (): PinDevice | null => {
  try {
    const raw = window.localStorage.getItem(PIN_DEVICE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.deviceId !== "string" || !parsed.deviceId) {
      return null;
    }
    return {
      deviceId: parsed.deviceId,
      email: typeof parsed.email === "string" ? parsed.email : "",
      name: typeof parsed.name === "string" ? parsed.name : "",
    };
  } catch {
    return null;
  }
};

export const setPinDevice = (device: PinDevice): void => {
  window.localStorage.setItem(PIN_DEVICE_KEY, JSON.stringify(device));
};

export const clearPinDevice = (): void => {
  window.localStorage.removeItem(PIN_DEVICE_KEY);
};
